import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Loading } from "@/components/ui/loading";
import { useDialogState } from "@/hooks/use-dialog-state";
import { FileExplorer } from "@/modules/images/components/file-explorer";
import { Route } from "@/routes/bikes/$bikeId/images";
import { OpenroadClientService } from "@/services/openroad-client.service";
import { DndContext, useDraggable, useDroppable } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core/dist/types";
import { CSS } from "@dnd-kit/utilities";
import { DialogTrigger } from "@radix-ui/react-dialog";
import { useQuery } from "@tanstack/react-query";
import { isEqual, uniq } from "lodash";
import { X } from "lucide-react";
import {
	FunctionComponent,
	PropsWithChildren,
	useEffect,
	useState,
} from "react";
import { toast } from "sonner";
import { BikeQueries } from "../queries/bike.queries";

export const BikeImagesRoute: FunctionComponent = () => {
	const { bikeId } = Route.useParams();

	const updateBikeMutation = BikeQueries.useUpdateBike();
	const { data: bike } = BikeQueries.useBike(bikeId);

	const addImageDialog = useDialogState();
	const { client } = OpenroadClientService.useState();

	const imagesQuery = useQuery({
		initialData: bike?.images ?? [],
		queryKey: ["bikes", bikeId, "images", bike?.id, bike?.images],
		queryFn: () => {
			return bike?.images ?? [];
		},
	});

	const [selectedFiles, setSelectedFiles] = useState<string[]>([]);

	useEffect(() => {
		if (bike != null) {
			setSelectedFiles(bike.images);
		}
	}, [bike]);

	const isDirty = !isEqual(imagesQuery.data, selectedFiles);

	const onSaveChangesPress = async () => {
		if (bike == null) {
			return;
		}

		await updateBikeMutation.mutateAsync({
			id: bike.id,
			images: selectedFiles,
		});

		toast.success("Bike images updated");
	};

	if (bike == null) {
		return <Loading />;
	}

	const onDragEnd = (event: DragEndEvent) => {
		const targetIndex = selectedFiles.indexOf(event.active.id as string);
		const destinationIndex = selectedFiles.indexOf(event.over?.id as string);

		if (targetIndex === -1 || destinationIndex === -1) {
			return;
		}

		setSelectedFiles((prev) => {
			const newFiles = [...prev];

			newFiles.splice(targetIndex, 1);
			newFiles.splice(destinationIndex, 0, event.active.id as string);

			return newFiles;
		});
	};

	const onSelectImages = (images: string[]) => {
		setSelectedFiles((prev) => uniq([...prev, ...images]));
		addImageDialog.closeDialog();
	};

	const onRemoveImagePress = (path: string) => {
		setSelectedFiles(selectedFiles.filter((f) => f !== path));
	};

	return (
		<div className={"flex-1 flex flex-col gap-4 relative"}>
			<div className={"flex gap-4 justify-between"}>
				<Dialog {...addImageDialog}>
					<DialogTrigger asChild>
						<Button variant={"secondary"}>Add images</Button>
					</DialogTrigger>
					<DialogContent
						className={"w-full h-full max-h-screen max-w-screen p-0"}
					>
						<FileExplorer
							onSelectPress={onSelectImages}
							onCancel={() => addImageDialog.closeDialog()}
						/>
					</DialogContent>
				</Dialog>
				<div className={"flex gap-4"}>
					<Button
						variant={"secondary"}
						onClick={() => setSelectedFiles(bike.images)}
						disabled={!isDirty}
					>
						Reset
					</Button>
					<Button
						onClick={onSaveChangesPress}
						isLoading={updateBikeMutation.isPending}
						disabled={!isDirty || updateBikeMutation.isPending}
					>
						Save changes
					</Button>
				</div>
			</div>

			<DndContext onDragEnd={onDragEnd}>
				<div className={"grid grid-cols-3 w-full gap-x-4 gap-y-4"}>
					{selectedFiles.map((path, index) => {
						const imagePath = selectedFiles[index];
						const { publicUrl } = client.supabase.getFile(imagePath);

						return (
							<Droppable key={path} id={path}>
								<Draggable id={imagePath} onClick={() => console.log("remove")}>
									<div
										className={
											"group w-full aspect-square rounded overflow-hidden relative"
										}
									>
										<img
											src={publicUrl}
											alt={publicUrl}
											className={"w-full h-full object-cover"}
										/>

										<div
											className={
												"cursor-pointer absolute top-0 right-0 p-2 bg-gray-800/50 text-white hover:bg-gray-800/80 z-[1000]"
											}
											onMouseDown={() => {
												onRemoveImagePress(imagePath);
											}}
										>
											<X className={"h-4 w-4"} />
										</div>

										<div
											className={
												"absolute bottom-0 right-0 p-2 bg-gray-800/50 text-white "
											}
										>
											{index + 1}
										</div>
									</div>
								</Draggable>
							</Droppable>
						);
					})}
				</div>
			</DndContext>
		</div>
	);
};

export function Droppable(props: PropsWithChildren<{ id: string }>) {
	const { isOver, setNodeRef } = useDroppable({
		id: props.id,
	});
	const style = {
		opacity: isOver ? 0.5 : 1,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={
				"border aspect-square flex justify-center items-center bg-gray-800/10 rounded"
			}
		>
			{props.children}
		</div>
	);
}
function Draggable(
	props: PropsWithChildren<{ id: string; onClick: () => void }>,
) {
	const { id, children } = props;

	const { attributes, listeners, setNodeRef, transform } = useDraggable({
		id,
	});
	const style = {
		// Outputs `translate3d(x, y, 0)`
		transform: CSS.Translate.toString(transform),
	};

	return (
		<div ref={setNodeRef} style={style} {...listeners} {...attributes}>
			{children}
		</div>
	);
}
